'use client';

import Box from '../../../atoms/Box/Box';
import Button from '../../../atoms/Button/Button';
import { useFormContext } from '../FormContext';
import type { ButtonGroupField, ButtonGroupButton, FormField } from '../types';
import type { FieldValues } from 'react-hook-form';
import { sanitizeData } from '../utils';
import styles from '../Form.module.scss';

export type ButtonGroupControlProps = {
  field: ButtonGroupField;
  theme: 'dark' | 'light';
  watchedValues: FieldValues;
  formFields: FormField[];
  locale: string;
  onSubmit: () => void;
};

/**
 * Form control for button groups with left/right positioning support
 * @component
 * @param {ButtonGroupControlProps} props - Component props
 * @returns {JSX.Element} ButtonGroup control component
 */
const ButtonGroupControl = ({
  field,
  theme,
  watchedValues,
  formFields,
  locale,
  onSubmit,
}: ButtonGroupControlProps) => {
  const { disabled: formDisabled, submitDisabled } = useFormContext();
  const leftButtons = field.buttons.filter((btn) => btn.position === 'left');
  const rightButtons = field.buttons.filter((btn) => btn.position !== 'left');
  const hasPositionedButtons = leftButtons.length > 0 && rightButtons.length > 0;

  const renderButton = (btn: ButtonGroupButton) => {
    const isSubmitButton = !btn.onClick;
    const isDisabled = btn.disabled || formDisabled || (isSubmitButton && submitDisabled);

    const handleBtnClick = async () => {
      // Prevent execution if button is disabled
      if (isDisabled) {
        return;
      }

      if (btn.onClick) {
        // Sanitize data before passing to custom onClick handler
        const sanitizedData = sanitizeData({ formFields, data: watchedValues, locale });
        await btn.onClick(sanitizedData);
      } else {
        // Validate and submit form
        onSubmit();
      }
    };

    return (
      <Button
        key={`button_${btn.id}`}
        label={btn.label}
        color={btn.color}
        theme={theme}
        size={btn.size}
        type={btn.variant || btn.type}
        isFluid={btn.isFluid}
        disabled={isDisabled}
        handleClick={handleBtnClick}
        gaComponent=""
        dataTestId={`button-${btn.id}`}
      />
    );
  };

  if (hasPositionedButtons) {
    return (
      <Box className={styles.buttonGroupSplit} key={`form_field_${field.id}`}>
        <Box className={styles.buttonGroupLeftSection}>{leftButtons.map(renderButton)}</Box>
        <Box className={styles.buttonGroupRightSection}>{rightButtons.map(renderButton)}</Box>
      </Box>
    );
  }

  return (
    <Box
      className={
        styles[
          `buttonGroup${field.align ? field.align.charAt(0).toUpperCase() + field.align.slice(1) : 'Right'}`
        ]
      }
      key={`form_field_${field.id}`}
    >
      {field.buttons.map(renderButton)}
    </Box>
  );
};

export default ButtonGroupControl;
